import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../services/supabaseClient';
import PlacesAutocomplete from '../common/PlacesAutocomplete';
import { useGoogleMapsApi } from '../../services/googleMapsLoader';
import FallbackMap from './FallbackMap';
import './VenueMap.css';

// Import Google Maps components directly
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';

/**
 * VenueMap component for displaying venues and gigs on a map
 * Only shows venue locations, never artist locations (for privacy)
 * 
 * @param {Object} props - Component props
 * @param {Object} props.center - Center coordinates for the map
 * @param {number} props.zoom - Zoom level for the map
 * @param {Array} props.highlightedGigs - Optional array of gigs to highlight on the map
 * @param {Array} props.highlightedVenues - Optional array of venues to highlight on the map
 * @param {Function} props.onMarkerClick - Optional callback for when a marker is clicked
 */
const VenueMap = ({ 
  center, 
  zoom = 10, 
  highlightedGigs = [], 
  highlightedVenues = [],
  onMarkerClick = null
}) => {
  const [venues, setVenues] = useState([]);
  const [gigs, setGigs] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [mapRef, setMapRef] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use our centralized Google Maps loader
  const { isLoaded, error: loadError } = useGoogleMapsApi();

  // Fetch venues and gigs from Supabase
  const fetchVenues = useCallback(async () => {
    try {
      // If we have highlighted venues or gigs, we don't need to fetch from Supabase
      if (highlightedVenues.length > 0 || highlightedGigs.length > 0) {
        if (highlightedVenues.length > 0) {
          setVenues(highlightedVenues.filter(venue => 
            venue.location_lat && venue.location_lng && venue.user_type === 'venue'
          ));
        }
        
        if (highlightedGigs.length > 0) {
          setGigs(highlightedGigs.filter(gig => 
            gig.location_lat && gig.location_lng
          ));
        }
        
        console.log('Using highlighted venues/gigs:', { 
          venues: highlightedVenues.length, 
          gigs: highlightedGigs.length 
        });
        
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      
      // Only fetch venues with location coordinates
      const { data: venueData, error: venueError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_type', 'venue')
        .not('location_lat', 'is', null)
        .not('location_lng', 'is', null);
      
      if (venueError) throw venueError;
      
      console.log('Fetched venues:', venueData);
      setVenues(venueData);
      
      // Fetch all gigs and filter for those with valid coordinates
      const { data: gigData, error: gigError } = await supabase
        .from('gigs')
        .select('*');
      
      if (gigError) throw gigError;
      
      // Filter gigs that have valid location coordinates and normalize the data
      const validGigs = gigData.filter(gig => {
        // Check if gig has valid coordinates
        const hasValidCoordinates = (
          gig.location_lat && gig.location_lng && 
          !isNaN(parseFloat(gig.location_lat)) && !isNaN(parseFloat(gig.location_lng))
        );
        
        if (!hasValidCoordinates) {
          console.log('Gig missing coordinates:', gig.id, gig.title);
        }
        
        return hasValidCoordinates;
      }).map(gig => {
        // Normalize coordinates to ensure they're numbers
        return {
          ...gig,
          location_lat: Number(gig.location_lat),
          location_lng: Number(gig.location_lng)
        };
      });
      
      console.log('Current gigs data:', gigData);
      console.log('Normalized gigs with coordinates:', validGigs);
      setGigs(validGigs);
      
    } catch (error) {
      console.error('Error fetching map data:', error);
      setError('Failed to load map data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [highlightedVenues, highlightedGigs]);

  // Get user's current location
  const getUserLocation = useCallback(() => {
    // Set default location immediately to prevent flickering
    const defaultLocation = { lat: 42.3314, lng: -83.0458 }; // Detroit coordinates
    
    // Only try to get location if we don't already have one
    if (!userLocation && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          // Handle geolocation errors silently - just use default location
          console.log('Geolocation not available or denied, using default location');
          // Only set if not already set
          if (!userLocation) {
            setUserLocation(defaultLocation);
          }
        },
        // Options to improve user experience
        { timeout: 10000, maximumAge: 60000, enableHighAccuracy: false }
      );
    } else if (!userLocation) {
      // Geolocation not supported or we're in a dependency cycle
      console.log('Geolocation not supported by this browser, using default location');
      setUserLocation(defaultLocation);
    }
  }, [userLocation]);

  // Initialize map and fetch data
  useEffect(() => {
    // Only run once when the map is loaded
    if (isLoaded && !loadError) {
      console.log('Google Maps API loaded successfully');
      
      // Set default location first to prevent flickering
      if (!userLocation) {
        setUserLocation({ lat: 42.3314, lng: -83.0458 }); // Detroit coordinates
      }
      
      // Fetch venues only once
      if (venues.length === 0 && gigs.length === 0) {
        fetchVenues();
      }
      
      // Try to get user location only once
      if (!userLocation) {
        getUserLocation();
      }
    } else if (loadError) {
      console.error('Google Maps API failed to load:', loadError);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, loadError]);
  
  // Fit map bounds to include all gig markers when they change
  useEffect(() => {
    console.log('Map bounds effect running, mapRef exists:', !!mapRef, 'gigs:', gigs.length);
    
    if (mapRef && gigs.length > 0) {
      try {
        const bounds = new window.google.maps.LatLngBounds();
        let validMarkers = 0;
        
        // Add gig positions to bounds
        gigs.forEach(gig => {
          if (gig.location_lat && gig.location_lng) {
            const lat = Number(gig.location_lat);
            const lng = Number(gig.location_lng);
            
            if (!isNaN(lat) && !isNaN(lng)) {
              console.log(`Adding gig ${gig.id} to bounds: ${lat}, ${lng}`);
              bounds.extend({ lat, lng });
              validMarkers++;
            } else {
              console.warn(`Invalid coordinates for gig ${gig.id}: ${gig.location_lat}, ${gig.location_lng}`);
            }
          }
        });
        
        console.log(`Found ${validMarkers} valid markers for map bounds`);
        
        // Only fit bounds if we have markers
        if (!bounds.isEmpty()) {
          console.log('Fitting map to bounds to show all gig markers');
          mapRef.fitBounds(bounds);
        } else {
          console.warn('No valid coordinates for map bounds adjustment');
        }
      } catch (error) {
        console.error('Error setting map bounds:', error);
      }
    }
  }, [mapRef, gigs]);

  // Reference to timeout to avoid multiple executions
  const timeoutRef = useRef(null);
  
  // Handle map load
  const onMapLoad = useCallback((map) => {
    console.log('Map loaded successfully');
    setMapRef(map);
    
    // Clear any existing timeout to prevent multiple executions
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Force a re-render after map loads to ensure markers appear
    timeoutRef.current = setTimeout(() => {
      console.log('Timeout callback executing, gigs:', gigs.length);
      if (map && gigs.length > 0) {
        console.log('Adjusting map view to show gig markers');
        try {
          const bounds = new window.google.maps.LatLngBounds();
          let markersAdded = 0;
          
          // Add gig positions to bounds
          gigs.forEach(gig => {
            if (gig.location_lat && gig.location_lng) {
              const lat = Number(gig.location_lat);
              const lng = Number(gig.location_lng);
              
              if (!isNaN(lat) && !isNaN(lng)) {
                console.log(`Adding gig ${gig.id} to bounds: ${lat}, ${lng}`);
                bounds.extend({ lat, lng });
                markersAdded++;
              } else {
                console.warn(`Invalid coordinates for gig ${gig.id}: ${gig.location_lat}, ${gig.location_lng}`);
              }
            }
          });
          
          console.log(`Added ${markersAdded} gig positions to map bounds`);
          
          if (!bounds.isEmpty()) {
            console.log('Fitting map to bounds');
            map.fitBounds(bounds);
            
            // Force a re-render of markers
            setGigs(prevGigs => [...prevGigs]);
          } else {
            console.warn('No valid coordinates to set map bounds');
          }
        } catch (error) {
          console.error('Error adjusting map bounds:', error);
        }
      }
    }, 1000); // Increased timeout to 1000ms
  }, [gigs]);

  // Handle marker click
  const handleMarkerClick = (place) => {
    setSelectedPlace(place);
    
    // If parent component provided a callback, call it
    if (onMarkerClick && typeof onMarkerClick === 'function') {
      onMarkerClick(place);
    }
  };

  // Render loading state
  if (!isLoaded || isLoading) {
    return <div className="map-loading">Loading map...</div>;
  }

  // Render error state
  if (loadError || error) {
    return (
      <div className="map-error">
        <p>Error: {loadError?.message || error}</p>
        <p>Please make sure you have installed @react-google-maps/api and set up your Google Maps API key.</p>
      </div>
    );
  }
  
  // If Google Maps components aren't available
  if (!GoogleMap) {
    return (
      <div className="map-error">
        <p>Google Maps components not available.</p>
        <p>Please install @react-google-maps/api: npm install @react-google-maps/api</p>
      </div>
    );
  }

  return (
    <div className="venue-map-container">
      {/* Location search input */}
      <div className="map-search-container">
        <PlacesAutocomplete
          placeholder="Enter location or zip code"
          onChange={(address, locationData) => {
            if (locationData && locationData.lat && locationData.lng) {
              setUserLocation({
                lat: locationData.lat,
                lng: locationData.lng
              });
            }
          }}
        />
      </div>
      
      {/* Show loading state */}
      {isLoading && (
        <div className="map-loading">Loading map data...</div>
      )}

      {/* Show error state */}
      {error && (
        <div className="map-error">Error loading map data: {error.message}</div>
      )}

      {/* Show Google Maps if loaded and no errors */}
      {isLoaded && !isLoading && !error && !loadError ? (
        <GoogleMap
          mapContainerClassName="venue-map"
          center={center || userLocation || { lat: 42.3314, lng: -83.0458 }} // Default to Detroit
          zoom={zoom}
          onLoad={onMapLoad}
          options={{
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: true,
            zoomControl: true,
            gestureHandling: 'cooperative', // Makes it easier to use on mobile
            clickableIcons: false, // Disable default POI markers
            styles: [
              {
                featureType: "poi",
                elementType: "labels",
                stylers: [{ visibility: "off" }] // Hide POI labels to avoid clutter
              }
            ]
          }}
        >
          {/* No venue markers - only showing gig locations */}

          {/* Render gig markers */}
          {console.log(`Attempting to render ${gigs.length} gig markers on map`)}
          {gigs.length > 0 && gigs.map((gig, index) => {
            if (!gig) {
              console.error('Encountered null or undefined gig in array');
              return null;
            }
            
            // Ensure coordinates are numbers
            const lat = Number(gig.location_lat);
            const lng = Number(gig.location_lng);
            
            console.log(`Rendering gig marker ${index+1}/${gigs.length}:`, gig.id, gig.title, 'Coordinates:', lat, lng);
            
            // Skip invalid coordinates
            if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) {
              console.error('Invalid coordinates for gig:', gig.id, gig.title, 'Raw values:', gig.location_lat, gig.location_lng);
              return null;
            }
            
            // Define a more visible marker icon with explicit dimensions
            const enhancedMarkerIcon = {
              url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
              scaledSize: new window.google.maps.Size(40, 40),
              origin: new window.google.maps.Point(0, 0),
              anchor: new window.google.maps.Point(20, 40),
              zIndex: 1000 + index
            };
            
            console.log(`Creating marker for gig ${gig.id} at position:`, { lat, lng });
            
            // Use a try-catch to handle any rendering errors
            try {
              return (
                <Marker
                  key={`gig-${gig.id}`}
                  position={{ lat, lng }}
                  icon={enhancedMarkerIcon}
                  zIndex={1000 + index} // Ensure high z-index
                  onClick={() => handleMarkerClick({ ...gig, type: 'gig' })}
                  options={{ 
                    optimized: false, // Disable marker optimization for better visibility
                    visible: true // Explicitly set visibility
                  }}
                />
              );
            } catch (error) {
              console.error(`Error rendering marker for gig ${gig.id}:`, error);
              return null;
            }
          })}

          {/* Render info window for selected place */}
          {selectedPlace && (
            <InfoWindow
              position={{
                lat: selectedPlace.location_lat,
                lng: selectedPlace.location_lng
              }}
              onCloseClick={() => setSelectedPlace(null)}
            >
              <div className="map-info-window">
                {selectedPlace.type === 'gig' ? (
                  <>
                    <h3>{selectedPlace.title || 'Gig'}</h3>
                    <p><strong>Date:</strong> {new Date(selectedPlace.date).toLocaleDateString()}</p>
                    <p><strong>Time:</strong> {selectedPlace.time}</p>
                    <p><strong>Location:</strong> {selectedPlace.location}</p>
                    <button 
                      className="btn btn-primary btn-sm mt-2"
                      onClick={() => window.location.href = `/gigs/${selectedPlace.id}`}
                    >
                      View Gig Details
                    </button>
                  </>
                ) : (
                  <>
                    <h3>{selectedPlace.venue_name || selectedPlace.full_name || 'Venue'}</h3>
                    <p>{selectedPlace.location || 'No address available'}</p>
                    {selectedPlace.website && (
                      <a href={selectedPlace.website} target="_blank" rel="noopener noreferrer">
                        Visit Website
                      </a>
                    )}
                    <button 
                      className="btn btn-primary btn-sm mt-2"
                      onClick={() => window.location.href = `/profile/${selectedPlace.id}`}
                    >
                      View Profile
                    </button>
                  </>
                )}
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      ) : (
        // Show fallback map if Google Maps failed to load or has errors
        <FallbackMap 
          venues={venues} 
          gigs={gigs} 
          onMarkerClick={(item) => {
            const type = item.venue_name ? 'venue' : 'gig';
            handleMarkerClick({ ...item, type });
          }}
        />
      )}
    </div>
  );
};

export default VenueMap;
